import { useAuth } from '@hb/react'
import {
  adminRoles,
  ErrorWithMessage,
  GoogleUserProps,
  User,
  USER_STATUS,
  UserCallback,
  UserGroup,
  UserRole,
} from '@hb/shared'
import {
  Auth,
  createUserWithEmailAndPassword,
  User as FirebaseUser,
  getMultiFactorResolver,
  getRedirectResult,
  GoogleAuthProvider,
  multiFactor,
  MultiFactorError,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import {
  onDisconnect,
  onValue,
  set as realtimeDbSet,
  ref,
  serverTimestamp,
} from 'firebase/database'
import { realtimeDb } from '../db'
import { dbGetOrCreateUser, populateUserData } from './utils'

// const [cookies, , removeCookie] = useCookies()
// const { inviteId } = cookies as { inviteId?: string }

const getCookie = (name: string) => {
  const match = new RegExp(`(^| )${name}=([^;]+)`).exec(document.cookie)
  if (match) return match[2]
  return null
}

const removeCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;`
}

const getUserCallback = (
  c: UserCallback,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
) => {
  const inviteId = getCookie('inviteId')
  const { success, error } = c
  if (inviteId) removeCookie('inviteId')
  if (success) {
    showSuccess(success)
  }
  if (error) {
    showError(error)
  }
}

const onSnapshot = (
  authU: GoogleUserProps,
  dbUser: Omit<User, 'id'>,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
) => {
  if (dbUser) {
    useAuth.setState({ user: populateUserData(authU, dbUser), loading: false })
  } else {
    getUserCallback({ error: 'Error getting user' }, showSuccess, showError)
  }
}

// this is called when the user goes online
// ie when the db connection is established AND the user is signed in
const onlineCallback = (user: FirebaseUser) => {
  const userStatusRealtimeRef = ref(realtimeDb, `${USER_STATUS}/${user.uid}`)
  void realtimeDbSet(userStatusRealtimeRef, {
    online: true,
    updated: serverTimestamp(),
  })

  void onDisconnect(userStatusRealtimeRef).set({
    online: false,
    updated: serverTimestamp(),
  })
}

const offlineCallback = async (user: FirebaseUser) => {
  const userStatusRealtimeRef = ref(realtimeDb, `${USER_STATUS}/${user.uid}`)
  await realtimeDbSet(userStatusRealtimeRef, {
    online: false,
    updated: serverTimestamp(),
  })
}

const connectionRef = ref(realtimeDb, '.info/connected')
onValue(connectionRef, snap => {
  const authUser = useAuth.getState().authUser
  if (!authUser) return
  const val = snap.val()
  if (val === false) return
  onlineCallback(authUser)
})

export const signInCallback = (
  newUser: FirebaseUser | null,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
  showInfo: (message: string) => void,
) => {
  const u = newUser
  if (u) {
    onlineCallback(u)
    const curr = useAuth.getState()
    if (curr.unsubscribe) curr.unsubscribe()
    useAuth.setState({ loading: true })

    // get multi factor enrollments
    const { enrolledFactors } = multiFactor(u)

    if (enrolledFactors.length === 0) {
      useAuth.setState({
        enrolledFactors,
        loading: false,
        enrollingUser: u,
      })
      return
    }

    void u
      .getIdTokenResult(true)
      .then(r => {
        let role: UserRole = 'user'
        if (r.claims?.superAdmin) role = 'super-admin'
        else if (r.claims?.leadAdmin) role = 'lead-admin'
        else if (r.claims?.admin) role = 'admin'
        const isPracticeMember = Object.keys(r.claims.practiceAccess ?? {}).length > 0
        let accessLevel: UserGroup = 'patient'
        if (adminRoles.includes(role)) accessLevel = 'admin'
        else if (isPracticeMember) accessLevel = 'practice'
        onlineCallback(u)
        useAuth.setState({
          claims: r.claims,
          role,
          enrollingUser: null,
          selectedEnrollmentFactor: null,
          enrolledFactors,
          authUser: u,
          admin:
            r.claims?.admin === true ||
            r.claims?.superAdmin === true ||
            r.claims?.leadAdmin === true,
          accessLevel,
        })
        if (u.emailVerified) {
          dbGetOrCreateUser(u, getUserCallback, onSnapshot, showSuccess, showError)
        } else {
          useAuth.setState({ loading: false })
        }
      })
      .catch(err => {
        console.error(err)
        showError('Error signing in.')
      })
  } else {
    if (useAuth.getState().user) showInfo('Signed out')
    useAuth.setState({
      loading: false,
      user: null,
      accessLevel: null,
      claims: null,
      role: null,
      admin: false,
      authUser: null,
      enrollingUser: null,
      captcha: null,
      enrolledFactors: null,
      multiFactorResolver: null,
      selectedEnrollmentFactor: null,
      unsubscribe: null,
    })
  }
}
// getRedirectResult(auth)
//   .then((res) => signInCallback(res?.user ?? null))
//   .catch((err) => {
//     console.error(err)
//   })

export const registerAuthListener = (
  auth: Auth,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
  showInfo: (message: string) => void,
) => onAuthStateChanged(auth, user => signInCallback(user, showSuccess, showError, showInfo))

export const registerRedirectResultListener = (
  auth: Auth,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
  showInfo: (message: string) => void,
) =>
  getRedirectResult(auth).then(res => {
    if (res?.user && res.operationType === 'signIn')
      signInCallback(res.user, showSuccess, showError, showInfo)
  })

export const signOut = async (auth: Auth) => {
  useAuth.setState({ loading: true })
  const { unsubscribe } = useAuth.getState()
  const currUser = auth.currentUser
  if (currUser) await offlineCallback(currUser)
  if (unsubscribe) unsubscribe()
  await auth.signOut()
}

export const handleAuthError = (
  auth: Auth,
  err: ErrorWithMessage,
  showError: (message: string) => void,
) => {
  if (err.code === 'auth/multi-factor-auth-required') {
    const resolver = getMultiFactorResolver(auth, err as MultiFactorError)
    useAuth.setState({
      multiFactorResolver: resolver,
      enrolledFactors: resolver.hints,
      enrollingUser: auth.currentUser,
    })
  } else {
    console.error(err)
    showError(err.message)
    throw new Error(err.message)
  }
}

export const signInWithGoogle = async (
  auth: Auth,
  showSuccess: (message: string) => void,
  showError: (message: string) => void,
  showInfo: (message: string) => void,
) => {
  // await setPersistence(auth, indexedDBLocalPersistence)
  const provider = new GoogleAuthProvider()
  try {
    const user = await signInWithPopup(auth, provider)
    signInCallback(user.user, showSuccess, showError, showInfo)
  } catch (err: any) {
    handleAuthError(auth, err, showError)
  }
}
export const signInWithEmailPassword = async ({
  auth,
  email,
  password,
  showSuccess,
  showError,
  showInfo,
}: {
  auth: Auth
  email: string
  password: string
  showSuccess: (message: string) => void
  showError: (message: string) => void
  showInfo: (message: string) => void
}) => {
  try {
    // await setPersistence(auth, indexedDBLocalPersistence)
    const user = await signInWithEmailAndPassword(auth, email, password)
    signInCallback(user.user, showSuccess, showError, showInfo)
  } catch (err: any) {
    handleAuthError(auth, err, showError)
  }
}

export const registerWithEmailPassword = async (
  auth: Auth,
  {
    email,
    password,
  }: {
    email: string
    password: string
  },
) =>
  // await setPersistence(auth, indexedDBLocalPersistence)
  createUserWithEmailAndPassword(auth, email, password)
